import { useRouter } from 'next/router'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useSite } from '@/lib/hooks'
import { getCookie, getUrlString, setCookie } from '@/lib/utils'
import { getSiteContextFromPath, getSiteModules, isWorkspaceModule } from '@/lib/utils/siteModules'
import {
  SiteContext as SiteContextType,
  SiteModule as SiteModuleType,
  SiteModules as SiteModulesType
} from '@/types'

interface ContextInterface extends SiteModulesType {
  routeContext: SiteContextType
  activeContext: SiteModuleType | null
  isContextReady: boolean
  // Módulos calculados a partir da resposta do servidor (e não apenas do fallback do ISR)
  isModulesReady: boolean
  setActiveContext: (module: SiteModuleType) => void
}

const SiteContextContext = createContext<ContextInterface>({
  store: false,
  enrollment: false,
  enrollmentWorkspace: false,
  defaultModule: null,
  hasMultipleModules: false,
  routeContext: null,
  activeContext: null,
  isContextReady: false,
  isModulesReady: false,
  setActiveContext: () => {}
})

interface Props {
  children: React.ReactNode
}

export const useSiteContext = () => useContext(SiteContextContext)

const contextCookieName = (siteSlug: string) => `@eskolare-shop/site_context_${siteSlug}`

function useProvideSiteContext() {
  // Hooks
  const router = useRouter()
  const { data: siteData, error: siteError, isValidating: isSiteValidating } = useSite()

  // Constants
  const siteSlug = getUrlString(router?.query?.site)
  const modules = useMemo(() => getSiteModules(siteData), [siteData])
  const routeContext = getSiteContextFromPath(router?.pathname)

  // States
  const [storedContext, setStoredContext] = useState<string>('')
  const [isContextReady, setIsContextReady] = useState(false)
  const [isModulesReady, setIsModulesReady] = useState(false)

  // Actions
  const setActiveContext = useCallback(
    (module: SiteModuleType) => {
      setStoredContext(module)
      if (siteSlug) setCookie(contextCookieName(siteSlug), module)
    },
    [siteSlug]
  )

  // Effects
  useEffect(() => {
    if (!siteSlug) return
    setStoredContext(getCookie(contextCookieName(siteSlug)) || '')
    setIsContextReady(true)
  }, [siteSlug])

  useEffect(() => {
    if (!!siteData && !isSiteValidating && !siteError) setIsModulesReady(true)
  }, [siteData, isSiteValidating, siteError])

  const isRouteModule = !!routeContext && modules[routeContext]
  const isRouteWorkspace = !!routeContext && isWorkspaceModule(modules, routeContext)

  useEffect(() => {
    if (isRouteWorkspace) setActiveContext(routeContext as SiteModuleType)
  }, [isRouteWorkspace, routeContext, setActiveContext])

  // Constants
  const activeContext: SiteModuleType | null = isRouteModule
    ? (routeContext as SiteModuleType)
    : isWorkspaceModule(modules, storedContext)
      ? (storedContext as SiteModuleType)
      : modules.defaultModule

  return {
    ...modules,
    routeContext,
    activeContext,
    isContextReady,
    isModulesReady,
    setActiveContext
  }
}

export default function SiteContextProvider({ children }: Props) {
  const siteContext = useProvideSiteContext()
  return <SiteContextContext.Provider value={siteContext}>{children}</SiteContextContext.Provider>
}
